import { basicPageProps } from '@/utils/isServer';
import { Breadcrumb, Button, Progress, Rate } from 'antd';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import usePrefix from '@/hooks/usePrefix';
import { pushAutoEvent } from '@/utils/gtag';
import { modalType } from '@/components/Modal';
import { useMobxStores } from '@/stores';
import { isTSA } from '@/utils/isTSA';
import dayjs from 'dayjs';
import reservationService from '@services/reservationService';
import { formatCurrency } from '@/helpers/format';
import { getParkingAtPath, getUrlCodeOrSlug } from '@/utils/newparkingatroutes';
import { cacheFetch } from '@/helpers/cacheFetch';
import { ArrowRightOutlined, CarOutlined, ClockCircleOutlined, FieldTimeOutlined, SafetyCertificateOutlined, StarFilled, TeamOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { defaultAirportCruiseCheckIn, defaultAirportCruiseCheckOut } from '@/utils/tools';


export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { configInfo, airports } = await basicPageProps(ctx);

  const tsa: string = ctx.params.market as string;

  if (!isTSA(tsa)) {
    return {
      redirect: {
        destination: '/airport-security-wait-times',
        permanent: false,
      },
    };
  }

  const tsadata = await cacheFetch(
    `${process.env.FORCE_BLOG_ADDRESS}/blog/tsa/${tsa.toUpperCase()}.json`
  )
  return {
    props: {
      market: tsa,
      airports,
      configInfo,
      tsadata,
    },
  };
};

const getProgressColor = (waitTime: number) => {
  if (waitTime <= 9) return '#15803d';
  if (waitTime <= 19) return '#3b82f6';
  if (waitTime <= 29) return '#fbbf24';
  return '#b91c1c';
};

function mapMarket(d) {
  if (d == 'DEN') return 'DIA';
  return d;
}

export default function Tsa({
  market,
  airports,
  configInfo,
  tsadata,
  ...props
}) {
  const [nearBy, setNearBy] = useState(null);
  const prefix = usePrefix();

  const groupedTerminals = Object.entries(
    tsadata['results']['open_close'].reduce((acc, [terminal, name, status]) => {
      if (!acc[terminal]) acc[terminal] = [];
      acc[terminal].push({ name, status });
      return acc;
    }, {})
  );

  useEffect(() => {
    const airport = airports.result
      .filter(x => x.airport_initials == mapMarket(market))
      .pop();
    if (airport) {
      reservationService
        .GetNearBy({
          airport_id: airport.airport_id,
          checkin: dayjs().add(defaultAirportCruiseCheckIn, 'day'),
          checkout: dayjs().add(defaultAirportCruiseCheckOut, 'day'),
        })
        .then((data) => setNearBy(data));
    }
  }, []);

  const stats = [
    { icon: TeamOutlined, value: "2M+", label: "travelers served" },
    { icon: SafetyCertificateOutlined, value: "4.8/5", label: "average rating" },
    { icon: StarFilled, value: "70%", label: "avg savings" },
  ];

  return (
    <div className="min-h-screen relative bg-[#FCFCFC]">
      <Head>
        <title>
          {tsadata['results']['airport_name']} ({tsadata['airport']}) TSA
          Security Wait Times | Live Updates
        </title>
        <meta
          name="description"
          content={`View real-time TSA security wait times for ${tsadata['results']['airport_name']} (${tsadata['airport']}). Get up-to-date info on wait times for standard and TSA PreCheck lanes.`}
          key="description"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.onairparking.com/"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "TSA Security Wait Times",
                  item: `https://www.onairparking.com/airport-security-wait-times/`
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: `${tsadata['results']['airport_name']} Security Wait Times`,
                  item: `https://www.onairparking.com/airport-security-wait-times/${tsadata['airport']}`
                }
              ]
            })
          }}
        />
      </Head>
      <div style={{ backgroundImage: 'linear-gradient(to bottom, #FBF5F1, #FCFCFC)' }}>
        <div className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
          <section className="p-5 md:py-10 justify-self-center" >
            <div className="container text-center px-5">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary mb-2 animate-pulse">
                <ThunderboltOutlined />
                🔥 {tsadata['results']['wait_time'] >= 30 ? "43" : "27"} travelers booked {tsadata.airport.toUpperCase()} parking in the last hour
              </div>
              <h1 className="font-display text-2xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
                {tsadata['results']['wait_time']} Min Wait at {tsadata.airport.toUpperCase()} — <br className="hidden md:block" />Don't Waste Time on Parking
              </h1>
              <p className="mt-2 text-sm md:text-lg max-w-xl mx-auto">
                Current security wait time at {tsadata['results']['city_name']}
              </p>

              <Link
                type="link"
                href={`/parking/${tsadata.airport.toUpperCase()}`}
                onClick={() => {
                  pushAutoEvent(
                    'tsa',
                    `newTSA-goToParking-${tsadata.airport.toUpperCase()}`,
                    'click'
                  )
                }}
              >
                <Button type="primary" size="large" className="mt-4 gap-2 h-14 md:h-12 text-base w-full md:w-auto shadow-lg shadow-primary/25 rounded-xl">
                  <CarOutlined /> Book {tsadata.airport.toUpperCase()} Parking — Save 70% <ArrowRightOutlined />
                </Button>
              </Link>
              <Breadcrumb className="justify-self-center text-sm md:text-base mt-4 mb-8">
                <Breadcrumb.Item>
                  <a onClick={() => pushAutoEvent("tsa", 'NewTSA-goToHome', "click")} href="/">Home</a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <a onClick={() => pushAutoEvent("tsa", 'NewTSA-backToTSA', "click")} href="/airport-security-wait-times">Security Wait Times</a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  {tsadata['results']['airport_name']}
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </section>
        </div>
      </div>

      <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="text-center rounded-xl border border-border/50 py-3 px-2 bg-gray-50">
              <s.icon className="text-base text-primary mx-auto mb-1" />
              <p className="text-lg font-bold text-foreground font-display">{s.value}</p>
              <p className="text-[10px]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">

        <div className="rounded-xl border border-border bg-white p-5 md:p-8 text-center mx-auto">
          <div className="mx-auto h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
            <ClockCircleOutlined className="text-lg text-primary" />
          </div>
          <p className="text-sm mb-2">
            Current Security Wait Time at {tsadata.airport.toUpperCase()}
          </p>
          <p className="text-4xl md:text-5xl font-bold text-foreground">{tsadata['results']['wait_time']} min</p>
          {tsadata['results']['wait_time'] >= 15 && (
            <p className="text-xs text-primary font-medium mt-2">⚡ High demand — book {tsadata.airport.toUpperCase()} parking before it fills up</p>
          )}
        </div>
      </section>
      <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
        {nearBy?.result?.length > 0 && (
          <div className="flex flex-col justify-center items-center space-y-4 py-4">
            <h2 className="font-display text-lg md:text-xl font-bold text-foreground mb-1 text-center">
              ⏰ Spots Filling Fast at {tsadata['results']['airport_name']}
            </h2>
            <p className="text-xs text-center mb-4">
              Pre-book and save up to 70% vs on-site airport rates.
            </p>
            <div
              className={`grid gap-6 w-full p-4 items-stretch ${nearBy?.result?.length === 1
                ? 'grid-cols-1 place-items-center'
                : nearBy?.result?.length === 2
                  ? 'grid-cols-1 xl:grid-cols-2 place-items-center'
                  : 'grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 justify-items-center'
                }`}
            >
              {nearBy?.result?.slice(0, 3).map((x) => (
                <>
                  <div
                    key={x.facility_id}
                    className="flex flex-col items-center justify-between w-full lg:max-w-lg bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition space-y-4"
                  >
                    <div className="flex flex-row items-center justify-between w-full space-x-4">
                      <div className="space-y-1 min-h-[4rem] flex-1 min-w-0">
                        <p className="text-base lg:text-lg font-semibold text-gray-900 truncate">
                          {x.facility_lot}
                        </p>
                        {x.highlights
                          ?.filter(
                            (highlight) =>
                              highlight.check &&
                              highlight.type == 'facility_shuttle_frequency'
                          )
                          .map((v) => (
                            <p key={v.type} className="text-sm text-gray-500">
                              Shuttle: {v.description}
                            </p>
                          ))}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {x.facilityParkingType == 3 ||
                          x.facilityParkingType == 4 ? (
                          <div className='flex flex-col items-end justify-center min-h-[3.5rem]'>
                            <span className="text-base lg:text-lg font-bold text-orange-600">
                              {formatCurrency({
                                value: x.facility_solid_selling_price
                                  ? x.facility_solid_selling_price
                                  : x.facility_afee,
                                locale: 'en-US',
                                currency: 'USD',
                              })}
                            </span>
                            <p className="text-xs text-gray-500 min-h-[1rem]">
                              {x.facilityParkingType == 3
                                ? 'Sleep and Park'
                                : 'Monthly Park'}
                            </p>
                          </div>
                        ) : (
                          <div className='flex flex-col items-end justify-center min-h-[3.5rem]'>
                            <span className="text-base lg:text-lg font-bold text-orange-600">
                              {formatCurrency({
                                value: x.facility_selling_price,
                                locale: 'en-US',
                                currency: 'USD',
                              })}
                            </span>

                            <p className="text-xs text-gray-500 min-h-[1rem]">
                              Daily Rate
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <Link
                      href={`${prefix}/${getParkingAtPath(x)}/${getUrlCodeOrSlug(
                        x
                      )}`}
                      onClick={() => {
                        pushAutoEvent(
                          'tsa',
                          `newTSA-goToParking-${tsadata.airport.toUpperCase()}`,
                          'click'
                        );
                      }}
                      className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded hover:bg-opacity-90 transition"
                    >
                      Book Now
                    </Link>
                  </div>
                </>
              ))}
            </div>
          </div>
        )}
        <div className="mt-5 rounded-xl border border-primary p-4 max-w-3xl mx-auto"
          style={{ backgroundImage: 'linear-gradient(to right, #FBF5F1, #FCFCFC)' }}>
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6">
            <div className="flex items-center gap-3 flex-1 text-center md:text-left">
              <div className="hidden md:flex h-10 w-10 rounded-full bg-primary/10 items-center justify-center shrink-0">
                <FieldTimeOutlined className="text-xl text-primary" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">⏰ Spots filling fast at {tsadata.airport.toUpperCase()}</p>
                <p className="text-xs mt-0.5">Planning to park at {tsadata['results']['airport_name']}? Get exclusive discounts.</p>
              </div>
            </div>
            <Link href={`/parking-coupons/${tsadata.airport.toUpperCase()}`}>
              <Button
                type="primary"
                size="large"
                className="w-full md:w-auto gap-2 h-12 md:h-10 text-base md:text-sm shadow-md shadow-primary/20"
                onClick={() => {
                  pushAutoEvent("tsa", `newTSA-goToCoupon-${tsadata.airport.toUpperCase()}`, "click");
                }}
              >
                <CarOutlined /> Lock In My Price <ArrowRightOutlined />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {groupedTerminals.length > 0 && (
        <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
          <div className="rounded-xl border border-border bg-white p-5 md:p-8 mx-auto">
            <h2 className="font-display text-lg md:text-xl font-bold text-foreground text-center mb-4">
              TSA Pre✓ at {tsadata.airport.toUpperCase()}
            </h2>
            <div className="divide-y">
              {groupedTerminals.map(([terminal, items]) => (
                <div key={terminal} className="py-3">
                  <h3 className="text-lg font-semibold">{terminal}</h3>
                  <div className="mt-1 divide-y ">
                    {(items as { name: string; status: string; }[]).map(({ name, status }) => (
                      <div key={name} className="flex items-center justify-between py-2">
                        <p className="text-xs">{name}</p>
                        <span className={`text-xs font-bold ${status === 'Closed' ? 'text-red-700' : 'text-green-700'}`}>
                          {status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {tsadata['results']['periods']?.length > 0 && (
        <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
          <div className="rounded-xl border border-border bg-white p-5 md:p-8 max-w-3xl mx-auto">
            <h2 className="font-display text-lg md:text-xl font-bold text-foreground text-center mb-1">
              Hourly Wait Times at  {tsadata['results']['airport_name']}
            </h2>
            <p className="text-xs text-center mb-5">
              {tsadata.airport.toUpperCase()} ·  {tsadata['results']['airport_name']}
            </p>
            <div className="space-y-2">
              {tsadata['results']['periods'].map(([name, waitTime]) => (
                <div key={name} className="flex items-center gap-3">
                  <p className="text-xs w-28 shrink-0 text-right">{name}</p>
                  <div className="flex-1 h-4 rounded-sm overflow-hidden relative">
                    <Progress
                      percent={Math.min((waitTime / 60) * 100, 100)}
                      showInfo={false}
                      strokeColor={getProgressColor(waitTime)}
                    />
                  </div>
                  <p className="text-xs w-16 shrink-0">Wait {waitTime} m</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-center mt-4">
              * Wait times are estimates, subject to change, and may not be indicative of your experience.
            </p>
          </div>
        </section>
      )}

      <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
        <div className="flex md:grid md:grid-cols-3 gap-3 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-hide -mx-1 px-1">
          {[
            { text: `Booked ${tsadata.airport.toUpperCase()} parking in 30 seconds. Saved $85!`, author: "Emily R." },
            { text: "The shuttle was waiting for us. Fastest airport experience ever.", author: "Marcus T." },
            { text: "Why did I ever pay airport prices? This is a no-brainer.", author: "Sarah L." },
          ].map((r) => (
            <div key={r.author} className="snap-start shrink-0 w-[80vw] md:w-auto rounded-xl border border-border bg-white p-4">
              <Rate disabled defaultValue={5} className="text-sm !text-primary mb-2" />
              <p className="text-sm text-foreground italic">"{r.text}"</p>
              <p className="mt-2 text-xs font-medium">— {r.author}</p>
            </div>
          ))}
        </div>
      </section>
      <h2 className="text-sm text-gray-600 font-medium text-center pb-4">
        *Based off of live <Link onClick={() => pushAutoEvent("tsa", `newTSA-goToTSAWebsite-${tsadata.airport.toUpperCase()}`, "click")} href={`https://tsawaittimes.com/security-wait-times/${tsadata.airport.toUpperCase()}`} type="link" className='underline text-primary'>TSA</Link> data.
      </h2>
    </div>
  );
}
